/**
 * Wire → UI normalization for the console overview payload.
 *
 * Il backend reale NON coincide col vecchio mock contract, e un secondo
 * contratto è in arrivo (stat sull'intera knowledge base). Questo modulo
 * accetta TUTTE le varianti note e le proietta nel modello UI stabile
 * (``ConsoleOverview``), tollerando campi mancanti o malformati:
 *
 *   - mock/UI shape:      { documents: 12, chunks, storage_bytes, ... }
 *   - BE odierno:         { documents: { total, chunks, storage_bytes }, ... }
 *   - contratto in arrivo: { documents, chunks, storage_bytes,
 *                            connectors_active, sources_breakdown?: [...] }
 *
 * Regole: un contatore assente → ``NaN`` (la UI rende "—"); 0 resta 0;
 * righe job/sync mappate campo-per-campo con alias (``job_id``/``filename``,
 * ``provider``/``source_type``/``last_sync_at``); MAI un throw.
 */

import type {
  ConsoleDocumentsPage,
  ConsoleLastSyncEntry,
  ConsoleOverview,
  ConsoleRecentJob,
  ConsoleSourceBreakdownEntry,
} from "./types";

// ── Loose readers ─────────────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Finite number or ``null`` (numeric strings accepted — BE serializers vary). */
function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

// ── Row mappers ───────────────────────────────────────────────────────────

function normalizeJob(value: unknown): ConsoleRecentJob {
  const row = asRecord(value) ?? {};
  return {
    id: asString(row.id) ?? asString(row.job_id) ?? "",
    status: asString(row.status) ?? "",
    document: asString(row.document) ?? asString(row.filename) ?? asString(row.document_id) ?? "",
    created_at: asString(row.created_at) ?? "",
  };
}

function normalizeSync(value: unknown): ConsoleLastSyncEntry {
  const row = asRecord(value) ?? {};
  const provider = asString(row.provider);
  const sourceType = asString(row.source_type);
  const at = asString(row.at) ?? asString(row.last_sync_at) ?? "";
  return {
    type: asString(row.type) ?? [provider, sourceType].filter(Boolean).join(" · "),
    at,
    // Il BE odierno non espone uno status per-sync: un timestamp presente
    // significa "ultima sync riuscita" → "ok"; senza timestamp lo stato è
    // ignoto e il badge rende il fallback neutro.
    status: asString(row.status) ?? (at ? "ok" : ""),
    ...(row.kind === "personal" || row.kind === "workspace" ? { kind: row.kind } : {}),
    ...(asString(row.connection_id) ? { connection_id: asString(row.connection_id) } : {}),
    ...(asString(row.label) ? { label: asString(row.label) } : {}),
  };
}

function normalizeBreakdown(value: unknown): ConsoleSourceBreakdownEntry[] | null {
  if (!Array.isArray(value)) return null;
  const entries = value
    .map((item): ConsoleSourceBreakdownEntry | null => {
      const row = asRecord(item);
      const source = row ? asString(row.source) : null;
      if (!source) return null;
      return { source, chunks: asNumber(row?.chunks) ?? Number.NaN };
    })
    .filter((entry): entry is ConsoleSourceBreakdownEntry => entry !== null);
  return entries.length > 0 ? entries : null;
}

// ── Overview ──────────────────────────────────────────────────────────────

export function normalizeConsoleOverview(raw: unknown): ConsoleOverview {
  const root = asRecord(raw) ?? {};
  // ``documents`` is a plain number in the flat contracts and a nested
  // ``{ total, chunks, storage_bytes }`` object in today's BE response.
  const docs = asRecord(root.documents);
  const breakdown = normalizeBreakdown(root.sources_breakdown);

  return {
    documents: asNumber(root.documents) ?? asNumber(docs?.total) ?? Number.NaN,
    chunks: asNumber(root.chunks) ?? asNumber(docs?.chunks) ?? Number.NaN,
    storage_bytes: asNumber(root.storage_bytes) ?? asNumber(docs?.storage_bytes) ?? Number.NaN,
    connectors_active: asNumber(root.connectors_active) ?? Number.NaN,
    recent_jobs: Array.isArray(root.recent_jobs) ? root.recent_jobs.map(normalizeJob) : [],
    last_sync_by_connector: Array.isArray(root.last_sync_by_connector)
      ? root.last_sync_by_connector.map(normalizeSync)
      : [],
    ...(breakdown ? { sources_breakdown: breakdown } : {}),
  };
}

/**
 * Normalizza una pagina di documenti.
 *
 * Il backend elenca ORA due origini nella stessa lista: gli upload della
 * piattaforma (``filename``) e i documenti del prodotto ingeriti dai
 * connettori (``title``, spesso senza estensione). La UI vuole un solo
 * campo ``name``: senza questa mappatura la tabella riceve ``undefined`` e
 * i formatter (estensione, label formato) esplodono a runtime.
 */
export function normalizeConsoleDocumentsPage(raw: unknown): ConsoleDocumentsPage {
  const root = (raw ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(root.documents) ? root.documents : [];
  const documents = rows.map((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const name =
      asString(row.name) ??
      asString(row.filename) ??
      asString(row.title) ??
      asString(row.document_id) ??
      "";
    return {
      ...(row as object),
      id: asString(row.id) ?? asString(row.document_id) ?? "",
      document_id: asString(row.document_id) ?? asString(row.id) ?? "",
      name,
      source: asString(row.source) ?? asString(row.origin) ?? "—",
      status: asString(row.status) ?? "unknown",
    } as unknown as ConsoleDocumentsPage["documents"][number];
  });
  return {
    ...(root as object),
    documents,
    total: asNumber(root.total) ?? documents.length,
    page_size: asNumber(root.page_size) ?? asNumber(root.limit) ?? 20,
    page:
      asNumber(root.page) ??
      Math.floor((asNumber(root.offset) ?? 0) / (asNumber(root.limit) ?? 20)) + 1,
  } as ConsoleDocumentsPage;
}
